import type { ProvinceDTO } from "../api/client";
import type {
  DistrictCreateInput,
  DistrictUpdateInput,
} from "@/modules/geo/domain/contracts";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import {
  Button,
  Checkbox,
  FormInput,
  FormRoot,
  RHF,
  zodResolver,
} from "@devhop/ui";
import type { Resolver, UseFormReturn } from "react-hook-form";
import { useCallback } from "react";
import { z } from "zod";

const PROVINCE_PAGE_SIZE = 20;

const DistrictFormSchema = z.object({
  provinceId: z.string().min(1, "ຕ້ອງເລືອກແຂວງ"),
  nameLo: z.string().min(1, "ຕ້ອງໃສ່ຊື່ (ລາວ)"),
  nameTh: z.string().optional(),
  nameEn: z.string().optional(),
  enabled: z.boolean().default(true),
  sortOrder: z.coerce.number().int().min(0).default(0),
});

export type DistrictFormValues = z.output<typeof DistrictFormSchema>;

const DEV_FILL: Omit<DistrictFormValues, "provinceId"> & {
  provinceId: string;
} = {
  provinceId: "",
  nameLo: "ໄຊເສດຖາ",
  nameTh: "ไชเสดถา",
  nameEn: "Saysettha",
  enabled: true,
  sortOrder: 0,
};

type DistrictFormProps = {
  provinces: ProvinceDTO[];
  initialValues?: Partial<DistrictFormValues>;
  onSubmit: (values: DistrictCreateInput | DistrictUpdateInput) => void;
  onCancel?: () => void;
  submitting?: boolean;
  onDevFill?: () => void;
};

function provinceName(p: ProvinceDTO): string {
  const n = p.name as { lo?: string; th?: string; en?: string };
  return n?.lo ?? n?.th ?? n?.en ?? p.id;
}

export function DistrictForm({
  provinces,
  initialValues,
  onSubmit,
  onCancel,
  submitting,
  onDevFill,
}: DistrictFormProps) {
  const methods = RHF.useForm<DistrictFormValues>({
    resolver: zodResolver(DistrictFormSchema) as Resolver<DistrictFormValues>,
    defaultValues: {
      provinceId: "",
      nameLo: "",
      nameTh: "",
      nameEn: "",
      enabled: true,
      sortOrder: 0,
      ...initialValues,
    },
  });

  const fillDev = () => {
    methods.reset({
      ...DEV_FILL,
      provinceId: provinces[0]?.id ?? "",
    });
  };

  const provinceQueryFn = useCallback(
    async ({
      search,
      pageParam = 1,
    }: {
      search: string;
      pageParam: number;
    }) => {
      const s = search.trim().toLowerCase();
      const filtered = s
        ? provinces.filter((p) => {
            const name = provinceName(p).toLowerCase();
            return name.includes(s);
          })
        : [...provinces];
      const page = pageParam > 0 ? pageParam : 1;
      const start = (page - 1) * PROVINCE_PAGE_SIZE;
      const items = filtered.slice(start, start + PROVINCE_PAGE_SIZE);
      const nextPage =
        start + PROVINCE_PAGE_SIZE < filtered.length ? page + 1 : null;
      return { items, nextPage };
    },
    [provinces]
  );

  const provincePreloadFn = useCallback(
    (id: string) => {
      const p = provinces.find((x) => x.id === id);
      return Promise.resolve(p ?? null);
    },
    [provinces]
  );

  return (
    <FormRoot<DistrictFormValues>
      methods={methods as UseFormReturn<DistrictFormValues>}
      onSubmit={(vals) =>
        onSubmit({
          provinceId: vals.provinceId,
          name: { lo: vals.nameLo, th: vals.nameTh, en: vals.nameEn },
          enabled: vals.enabled,
          sortOrder: vals.sortOrder,
        })
      }
    >
      <div className="space-y-4" data-tourid="geo-district-form">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-medium">ຂໍ້ມູນເມືອງ</h3>
          {onDevFill && (
            <Button type="button" variant="outline" size="sm" onClick={fillDev}>
              ເຕີມຂໍ້ມູນທົດສອບ
            </Button>
          )}
        </div>
        <div data-tourid="geo-form-province">
          <label className="mb-1 block font-medium text-sm">ແຂວງ</label>
          <RHF.Controller
            name="provinceId"
            control={methods.control}
            render={({ field }) => (
              <InfiniteCombobox<ProvinceDTO>
                queryKey={["geo", "provinces"]}
                queryFn={provinceQueryFn}
                preloadQueryFn={provincePreloadFn}
                getLabel={provinceName}
                getValue={(item) => item.id}
                value={field.value ?? ""}
                onValueChange={field.onChange}
                placeholder="— ເລືອກແຂວງ"
                clearable
              />
            )}
          />
        </div>
        <FormInput
          name="nameLo"
          label="ຊື່ (ລາວ)"
          placeholder="ພາສາລາວ"
          data-tourid="geo-form-nameLo"
        />
        <FormInput name="nameTh" label="ຊື່ (ไทย)" placeholder="ภาษาไทย" />
        <FormInput name="nameEn" label="ຊື່ (EN)" placeholder="English" />
        <div className="flex items-center gap-2" data-tourid="geo-form-enabled">
          <RHF.Controller
            name="enabled"
            control={methods.control}
            render={({ field }) => (
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            )}
          />
          <label className="font-medium text-sm">ເປີດສະແດງໃນ dropdown</label>
        </div>
        <FormInput name="sortOrder" label="ລຳດັບ" type="number" min={0} />
        <div className="flex gap-2 pt-2" data-tourid="geo-form-submit">
          <Button type="submit" disabled={submitting}>
            ບັນທຶກ
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel ?? (() => methods.reset())}
          >
            ຍົກເລີກ
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
